import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, Clock, ChevronLeft, User, MapPin } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';
import { LeadCaptureForm } from '@/components/forms/LeadCaptureForm';
import { format } from 'date-fns';
import { sanitizeHtml } from '@/lib/sanitize';
import { useLoanOfficerAd } from '@/hooks/useLoanOfficerAd';
import { gaEvents } from '@/hooks/useGoogleAnalytics';

interface BlogPostData {
  id: string;
  slug: string;
  title: string;
  category: string;
  topic: string;
  content_html: string;
  excerpt: string | null;
  main_image_url: string | null;
  main_image_alt: string | null;
  published_at: string;
  target_town_ids: string[] | null;
  target_area_ids: string[] | null;
  target_audience: 'buyer' | 'seller' | null;
}

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  favicon_url: string | null;
  theme: string;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
  mortgage_ad_image_url?: string | null;
}

interface RealtorData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  photo_url: string | null;
  logo_url: string | null;
  title_preference: string | null;
  ad_image_url: string | null;
}

interface Town {
  id: string;
  name: string;
  slug: string;
}

interface Area {
  id: string;
  name: string;
  slug: string;
}

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  main_image_url: string | null;
  published_at: string;
}

export default function BlogPost() {
  const { postSlug } = useParams<{ postSlug: string }>();
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get('preview') === 'true';
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [site, setSite] = useState<SiteData | null>(null);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [towns, setTowns] = useState<Town[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);

  // Loan officer ad - get town IDs from post targets or all towns
  const townIds = useMemo(() => {
    if (post?.target_town_ids && post.target_town_ids.length > 0) {
      return post.target_town_ids;
    }
    return towns.map(t => t.id);
  }, [post?.target_town_ids, towns]);
  
  const { data: loanOfficerAd } = useLoanOfficerAd(site?.id || null, townIds);
  const mortgageAdImageUrl = loanOfficerAd?.ad_image_url || site?.mortgage_ad_image_url;
  const mortgageAdAlt = loanOfficerAd 
    ? `${loanOfficerAd.first_name} ${loanOfficerAd.last_name} - NMLS: ${loanOfficerAd.license_number || 'N/A'} - Mortgage Services`
    : 'Mortgage Information and Tips';

  // Track blog view when post loads
  useEffect(() => {
    if (post && !isPreviewMode) {
      gaEvents.viewBlog(post.id, post.title, post.category, post.slug);
    }
  }, [post?.id, isPreviewMode]);

  useEffect(() => {
    if (postSlug) {
      fetchPost();
    }
  }, [postSlug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      // Resolve site
      const siteIdFromQuery = searchParams.get('siteId');

      let siteId: string;

      if (isPreviewMode && siteIdFromQuery) {
        // Admin preview: trust the explicit siteId to avoid hostname mismatches in preview domains.
        const { data: siteDataById } = await supabase
          .from('sites_public' as any)
          .select('*')
          .eq('id', siteIdFromQuery)
          .maybeSingle();

        if (!siteDataById) {
          setLoading(false);
          return;
        }

        setSite(siteDataById as unknown as SiteData);
        siteId = (siteDataById as any).id;
      } else {
        // Public: get site by domain
        const hostname = window.location.hostname.replace(/^www\./, '');
        let siteQuery = supabase
          .from('sites_public' as any)
          .select('*')
          .eq('status', 'active');

        if (!hostname.includes('localhost') && !hostname.includes('lovable.app')) {
          siteQuery = siteQuery.or(`primary_domain.eq.${hostname},primary_domain.eq.www.${hostname}`);
        }

        const { data: siteData } = await siteQuery.limit(1).maybeSingle();

        if (!siteData) {
          const { data: fallbackSite } = await supabase
            .from('sites_public' as any)
            .select('*')
            .eq('status', 'active')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (!fallbackSite) {
            setLoading(false);
            return;
          }
          setSite(fallbackSite as unknown as SiteData);
          siteId = (fallbackSite as any).id;
        } else {
          setSite(siteData as unknown as SiteData);
          siteId = (siteData as any).id;
        }
      }

      // Fetch the blog post
      // Allow previewing scheduled posts if:
      // 1. preview=true query param is set (from admin)
      // 2. User is logged in
      const { data: sessionData } = await supabase.auth.getSession();
      const allowScheduledPreview = isPreviewMode || !!sessionData.session;

      // 1) Try exact slug match
      let postQuery = supabase
        .from('blog_posts')
        .select('*')
        .eq('site_id', siteId)
        .eq('slug', postSlug)
        .eq('is_published', true);

      if (!allowScheduledPreview) {
        postQuery = postQuery.lte('published_at', new Date().toISOString());
      }

      let { data: postData } = await postQuery.maybeSingle();

      // 2) Fallback: allow legacy slugs that had a date suffix (e.g. "my-post-2026-01-17")
      if (!postData) {
        let fallbackQuery = supabase
          .from('blog_posts')
          .select('*')
          .eq('site_id', siteId)
          .eq('is_published', true)
          .like('slug', `${postSlug}-%`)
          .order('published_at', { ascending: false })
          .limit(1);

        if (!allowScheduledPreview) {
          fallbackQuery = fallbackQuery.lte('published_at', new Date().toISOString());
        }

        const fallback = await fallbackQuery.maybeSingle();
        postData = fallback.data ?? null;
      }

      if (!postData) {
        setLoading(false);
        return;
      }

      setPost(postData as BlogPostData);

      // Fetch tags for this post
      const { data: tagsData } = await supabase
        .from('blog_post_tags')
        .select('blog_tags(name)')
        .eq('blog_post_id', postData.id);

      if (tagsData) {
        setTags(tagsData.map((t: any) => t.blog_tags?.name).filter(Boolean));
      }

      // Fetch realtor and towns
      const [realtorRes, townsRes] = await Promise.all([
        supabase.from('realtor_profiles').select('*').eq('site_id', siteId).single(),
        supabase.from('towns').select('id, name, slug').eq('site_id', siteId).eq('is_published', true).order('name')
      ]);

      if (realtorRes.data) setRealtor(realtorRes.data as RealtorData);
      if (townsRes.data) {
        setTowns(townsRes.data);
        
        // If only one town, fetch its areas for the AreaNavBar
        if (townsRes.data.length === 1) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id, name, slug')
            .eq('town_id', townsRes.data[0].id)
            .eq('is_published', true)
            .order('name');
          
          if (areasData) setAreas(areasData);
        }
      }

      // Fetch related posts (same category, different post)
      let relatedQuery = supabase
        .from('blog_posts')
        .select('id, slug, title, main_image_url, published_at')
        .eq('site_id', siteId)
        .eq('category', postData.category)
        .eq('is_published', true)
        .neq('id', postData.id)
        .order('published_at', { ascending: false })
        .limit(3);

      if (!allowScheduledPreview) {
        relatedQuery = relatedQuery.lte('published_at', new Date().toISOString());
      }

      const { data: relatedData } = await relatedQuery;

      if (relatedData) setRelatedPosts(relatedData);

    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Estimate reading time (avg 200 words per minute)
  const getReadingTime = (html: string) => {
    const text = html.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-muted">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!site || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Post not found</p>
          <Link to="/blog" className="text-primary hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const showRealtor = site.realtor_assigned !== false && realtor;
  const pageTitle = `${post.title} | ${site.name}`;
  const realtorName = showRealtor ? [realtor?.first_name, realtor?.last_name].filter(Boolean).join(' ') : '';
  const canonicalUrl = `https://${window.location.hostname}/blog/${post.slug}`;
  const metaDescription = post.excerpt || `${post.title} - Expert real estate insights from ${realtorName || site.name}`;

  // Generate BreadcrumbList structured data for rich snippets
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `https://${window.location.hostname}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `https://${window.location.hostname}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": canonicalUrl
      }
    ]
  };

  // Generate Article structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": metaDescription,
    "image": post.main_image_url || undefined,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": realtorName ? {
      "@type": "Person",
      "name": realtorName
    } : {
      "@type": "Organization",
      "name": site.name
    },
    "publisher": {
      "@type": "Organization",
      "name": site.name,
      "logo": realtor?.logo_url ? {
        "@type": "ImageObject",
        "url": realtor.logo_url
      } : undefined
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "articleSection": post.category,
    "keywords": tags.join(", ")
  };

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font || 'lora'}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="author" content={realtorName || site.name} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:site_name" content={site.name} />
        <meta property="og:description" content={metaDescription} />
        {/* og:image is injected by Cloudflare worker */}
        {post.main_image_url && <meta property="og:image:alt" content={post.main_image_alt || post.title} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        
        {/* Article Meta */}
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:modified_time" content={post.published_at} />
        <meta property="article:section" content={post.category} />
        <meta property="article:author" content={realtorName || site.name} />
        {tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Structured Data - Breadcrumbs */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        
        {/* Structured Data - Article */}
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      </Helmet>

      <Header 
        siteName={site.name} 
        logoUrl={showRealtor ? realtor?.logo_url : undefined}
        faviconUrl={site.favicon_url}
        enableMarketValueReport={site.enable_market_value_report !== false}
        enableCommunityGuide={site.enable_community_guide !== false}
        enableMortgageInfo={site.enable_mortgage_info !== false}
      />

      <TownNavBar 
        towns={towns} 
        isPreview={false}
        enableMarketValueReport={site.enable_market_value_report !== false}
        enableMortgageInfo={site.enable_mortgage_info !== false}
        realtorFirstName={realtor?.first_name}
        themeId={site.theme}
      />
      
      {/* Area Navigation Bar - Show when only one town exists */}
      {towns.length === 1 && areas.length > 0 && (
        <AreaNavBar areas={areas} townSlug={towns[0].slug} />
      )}

      <main className="min-h-screen bg-background">
        <article className="py-8 md:py-12">
          <div className="container px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li>/</li>
                <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
                <li>/</li>
                <li className="text-foreground font-medium truncate max-w-[200px]">{post.title}</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-7">
                {/* Header */}
                <header className="mb-8">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                    <Badge variant="secondary">{post.category}</Badge>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <time dateTime={post.published_at}>
                        {format(new Date(post.published_at), 'MMMM d, yyyy \'at\' h:mm a')}
                      </time>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{getReadingTime(post.content_html)}</span>
                    </div>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                    {post.title}
                  </h1>

                  <p className="text-sm text-muted-foreground">
                    Topic: {post.topic}
                  </p>

                  {/* Tags */}
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {tags.map(tag => (
                        <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`}>
                          <Badge variant="outline" className="hover:bg-primary hover:text-primary-foreground cursor-pointer">
                            {tag}
                          </Badge>
                        </Link>
                      ))}
                    </div>
                  )}
                </header>

                {/* Featured Image */}
                {post.main_image_url && (
                  <figure className="mb-8 rounded-lg overflow-hidden">
                    <img 
                      src={post.main_image_url} 
                      alt={post.main_image_alt || post.title}
                      className="w-full h-auto object-cover aspect-video"
                    />
                    {post.main_image_alt && (
                      <figcaption className="text-xs text-muted-foreground mt-2 text-center">
                        {post.main_image_alt}
                      </figcaption>
                    )}
                  </figure>
                )}

                {/* Article Content */}
                <div 
                  className="prose prose-lg max-w-none 
                    prose-headings:font-display prose-headings:text-foreground
                    prose-p:text-muted-foreground prose-p:leading-relaxed
                    prose-a:text-accent prose-a:underline hover:prose-a:no-underline
                    prose-strong:text-foreground
                    prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                    prose-li:marker:text-accent"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content_html) }}
                />

                {/* Mentioned Towns — internal links to town pages */}
                {post.target_town_ids && post.target_town_ids.length > 0 && (() => {
                  const mentionedTowns = towns.filter(t => post.target_town_ids!.includes(t.id));
                  if (mentionedTowns.length === 0) return null;
                  return (
                    <div className="mt-8 pt-6 border-t border-border">
                      <h3 className="text-lg font-display font-semibold mb-4">Explore These Towns</h3>
                      <div className="flex flex-wrap gap-3">
                        {mentionedTowns.map(t => (
                          <Link key={t.id} to={`/${t.slug}`}>
                            <Badge variant="secondary" className="hover:bg-primary hover:text-primary-foreground transition-colors text-sm py-1.5 px-3">
                              <MapPin className="h-3.5 w-3.5 mr-1.5" />
                              {t.name}
                            </Badge>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-border">
                    <h3 className="text-lg font-display font-semibold mb-6">Related Articles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {relatedPosts.map(related => (
                        <Link key={related.id} to={`/blog/${related.slug}`} className="group">
                          <Card className="h-full overflow-hidden hover:shadow-md transition-shadow">
                            {related.main_image_url && (
                              <div className="aspect-video overflow-hidden">
                                <img 
                                  src={related.main_image_url} 
                                  alt={related.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            )}
                            <CardContent className="p-3">
                              <time className="text-xs text-muted-foreground">
                                {format(new Date(related.published_at), 'MMM d, yyyy')}
                              </time>
                              <h4 className="font-medium text-sm mt-1 group-hover:text-primary transition-colors line-clamp-2">
                                {related.title}
                              </h4>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back to Blog */}
                <div className="mt-8">
                  <Link to="/blog" className="inline-flex items-center text-primary hover:underline">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back to Blog
                  </Link>
                </div>
              </div>

              {/* Sidebar - sticky container */}
              <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start space-y-6">
                {showRealtor && <RealtorCard realtor={realtor} adTypeFilter={post.target_audience} />}

                <Card className="p-6">
                  <h3 className="text-xl font-display font-bold mb-2 text-center">
                    Have Questions?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 text-center">
                    Let's discuss your real estate needs.
                  </p>
                  <LeadCaptureForm 
                    siteId={site.id}
                    pagePath={`/blog/${postSlug || post.slug}`}
                    siteDomain={window.location.hostname}
                  />
                </Card>

                {/* Mortgage Info Ad */}
                {site.enable_mortgage_info !== false && mortgageAdImageUrl && (
                  <Link to="/mortgages" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                    <img 
                      src={mortgageAdImageUrl} 
                      alt={mortgageAdAlt} 
                      className="w-full h-auto object-cover"
                    />
                  </Link>
                )}
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer 
        siteName={site.name} 
        locationServed={site.location_served} 
        towns={towns}
        areas={areas}
        realtor={showRealtor ? realtor : null}
        enableBuyers={true}
        enableSellers={true}
        enableMortgageInfo={site.enable_mortgage_info !== false}
      />
    </PublicThemeProvider>
  );
}